<div class="pt-20 sm:pt-24 md:pt-28 lg:pt-32">
  <div class="ms-10">
    <shared-arrow-back></shared-arrow-back>
  </div>

  <div class="container mx-auto px-4 py-8">
    <h1 class="text-3xl font-bold text-gray-800 mb-6">Mis Órdenes</h1>

    <!-- Error state -->
    @if (error) {
    <div class="text-center py-8">
      <p class="text-red-600">{{ error }}</p>
    </div>
    }

    <!-- Skeleton loading state -->
    @if (loading) {
    <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
      @for (item of [1, 2, 3, 4, 5, 6]; track item) {
      <div
        class="bg-white rounded-lg shadow-md overflow-hidden border border-gray-200 animate-pulse"
      >
        <div class="relative">
          <div class="w-full h-48 bg-gray-200"></div>
          <div
            class="absolute top-4 right-4 w-20 h-6 bg-gray-200 rounded-full"
          ></div>
        </div>
        <div class="p-5">
          <div class="h-6 bg-gray-200 rounded w-3/4 mb-3"></div>
          <div class="space-y-3">
            <div class="flex items-center">
              <div class="w-5 h-5 bg-gray-200 rounded mr-2"></div>
              <div class="h-4 bg-gray-200 rounded w-1/2"></div>
            </div>
            <div class="flex items-center">
              <div class="w-5 h-5 bg-gray-200 rounded mr-2"></div>
              <div class="h-4 bg-gray-200 rounded w-2/3"></div>
            </div>
            <div class="flex items-center">
              <div class="w-5 h-5 bg-gray-200 rounded mr-2"></div>
              <div class="h-4 bg-gray-200 rounded w-1/4"></div>
            </div>
          </div>
          <div class="mt-4 pt-4 border-t border-gray-200">
            <div class="flex items-center justify-between">
              <div class="flex items-center space-x-2">
                <div class="w-5 h-5 bg-gray-200 rounded"></div>
                <div class="h-4 bg-gray-200 rounded w-3/4"></div>
              </div>
              <div class="w-5 h-5 bg-gray-200 rounded"></div>
            </div>
          </div>
        </div>
      </div>
      }
    </div>
    }

    <!-- Empty state - No orders -->
    @if (!loading && !error && orders.length === 0) {
    <div class="text-center py-16 px-4">
      <div
        class="max-w-md mx-auto bg-white rounded-xl shadow-md overflow-hidden p-8 border border-gray-100"
      >
        <div class="flex flex-col items-center">
          <div class="mb-6 text-yellow-500">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              class="h-24 w-24"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="1.5"
                d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z"
              />
            </svg>
          </div>
          <h2 class="text-2xl font-bold text-gray-800 mb-2">
            No hay órdenes disponibles
          </h2>
          <p class="text-gray-600 text-center">
            Cuando realices un pedido, aparecerá en esta sección
          </p>
        </div>
      </div>
    </div>
    }

    <!-- Orders grid -->
    @if (!loading && !error && orders.length > 0) {
    <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
      @for (order of orders; track order) {
      <div
        #orderCard
        (click)="visitOrden(order.id_order)"
        (mouseenter)="onCardEnter($event)"
        (mouseleave)="onCardLeave($event)"
        class="cursor-pointer bg-white rounded-lg shadow-md overflow-hidden border border-gray-200"
      >
        <!-- Imagen y badge de estado -->
        <div class="relative">
          <img
            [src]="getImageUrl(order.picture_logo)"
            [alt]="order.name_restaurant"
            #orderImage
            class="w-full h-48 object-cover"
            (mouseenter)="onImageEnter($event)"
            (mouseleave)="onImageLeave($event)"
            (error)="onImageError($event)"
          />
          <div
            [class]="
              'absolute top-4 right-4 text-white text-xs font-bold px-3 py-1 rounded-full ' +
              getStatusClass(order.status)
            "
          >
            {{ order.status }}
          </div>
        </div>
        <!-- Contenido de la tarjeta -->
        <div class="p-5">
          <h2 class="text-xl font-bold text-gray-800 mb-3">
            {{ order.name_restaurant }}
          </h2>
          <div class="space-y-3">
            <!-- Número de orden -->
            <div class="flex items-center">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                class="h-5 w-5 text-gray-500 mr-2"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
                />
              </svg>
              <span class="text-gray-700">Orden #{{ order.no_order }}</span>
            </div>
            <!-- Tiempo estimado -->
            <div class="flex items-center">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                class="h-5 w-5 text-gray-500 mr-2"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              <span class="text-gray-700"
                >Tiempo estimado de preparación:
                {{ order.time_order || "No disponible" }}</span
              >
            </div>
            <!-- Método de pago -->
            <div class="flex items-center">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                class="h-5 w-5 text-gray-500 mr-2"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z"
                />
              </svg>
              <span class="text-gray-700">{{ order.payment }}</span>
            </div>
          </div>
          <!-- Dirección con flecha -->
          <div class="mt-4 pt-4 border-t border-gray-200">
            <div class="flex items-center justify-between">
              <div class="flex items-center">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  class="h-5 w-5 text-gray-500 mr-2"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                  />
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                  />
                </svg>
                <span
                  class="text-gray-700 truncate max-w-[200px] inline-block"
                  >{{ order.address }}</span
                >
              </div>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                class="h-5 w-5 text-gray-400 js-arrow-ico"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M9 5l7 7-7 7"
                />
              </svg>
            </div>
          </div>
        </div>
      </div>
      }
    </div>
    }
  </div>
</div>
